export function normaliseText(value) {
  return String(value || "").trim();
}

export function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  if (typeof value._seconds === "number") return new Date(value._seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatDate(value, includeTime = false) {
  const date = toDate(value);
  if (!date) return "—";
  return date.toLocaleString("en-GB", {
    timeZone: "Europe/London",
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(includeTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
}

export function agreementState(item = {}) {
  const status = String(item.status || "pending").toLowerCase();
  const activation = String(item.activationStatus || "").toLowerCase();
  if (item.adminActivatedAt || activation === "activated" || status === "active") {
    return { key: "active", label: "Active", detail: "Account activated", score: 100 };
  }
  if (status === "signed") {
    return { key: "signed", label: "Signed", detail: "Awaiting activation", score: 78 };
  }
  if (status === "opened") {
    return { key: "opened", label: "Opened", detail: "Awaiting signature", score: 52 };
  }
  if (status === "expired" || status === "revoked") {
    return { key: status, label: status === "revoked" ? "Revoked" : "Expired", detail: "Action required", score: 18 };
  }
  return { key: "pending", label: "Pending", detail: "Invite not yet opened", score: 34 };
}

export function buildAgreementStats(items = []) {
  return items.reduce(
    (acc, item) => {
      const state = agreementState(item).key;
      acc.total += 1;
      if (Object.prototype.hasOwnProperty.call(acc, state)) acc[state] += 1;
      return acc;
    },
    { total: 0, pending: 0, opened: 0, signed: 0, active: 0, expired: 0, revoked: 0 }
  );
}

export function buildDemoStats(items = []) {
  return items.reduce(
    (acc, item) => {
      const status = String(item.status || "new").toLowerCase().replace(/\s+/g, "_");
      acc.total += 1;
      if (status === "contacted") acc.contacted += 1;
      else if (["meeting_booked", "booked"].includes(status)) acc.booked += 1;
      else if (["demo_completed", "completed"].includes(status)) acc.completed += 1;
      else if (status === "closed") acc.closed += 1;
      else acc.new += 1;
      return acc;
    },
    { total: 0, new: 0, contacted: 0, booked: 0, completed: 0, closed: 0 }
  );
}

export function customerHealth(items = []) {
  const active = items.filter((item) => agreementState(item).key === "active").length;
  const signed = items.filter((item) => agreementState(item).key === "signed").length;
  const attention = items.filter((item) => ["expired", "revoked"].includes(agreementState(item).key)).length;
  return {
    healthy: active,
    onboarding: Math.max(0, items.length - active - attention),
    attention,
    activationRate: items.length ? Math.round(((active + signed * 0.5) / items.length) * 100) : 0,
  };
}

export function recentActivity(agreements = [], demos = [], limit = 8) {
  const agreementRows = agreements.map((item) => {
    const state = agreementState(item);
    const date = toDate(item.updatedAt || item.adminActivatedAt || item.signedAt || item.openedAt || item.createdAt || item.invitedAt);
    return {
      id: `agreement-${item.inviteId || item.id || item.schoolId || Math.random()}`,
      type: "agreement",
      title: item.schoolName || "School agreement",
      description: `${state.label} · ${item.recipientEmail || "School representative"}`,
      date,
    };
  });
  const demoRows = demos.map((item) => ({
    id: `demo-${item.id || Math.random()}`,
    type: "demo",
    title: item.schoolName || item.organisationName || item.name || "New demo request",
    description: `${String(item.status || "new").replace(/_/g, " ")} · ${item.email || item.contactEmail || "New lead"}`,
    date: toDate(item.updatedAt || item.createdAt || item.submittedAt),
  }));
  return [...agreementRows, ...demoRows]
    .sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0))
    .slice(0, limit);
}
